import { useMemo, useState } from "react";
import { Link } from "react-router";
import { Search } from "lucide-react";

import { DataTable } from "../components/DataTable.jsx";
import { FilterBar } from "../components/FilterBar.jsx";
import { PageHeader } from "../components/PageHeader.jsx";
import { StatSummary } from "../components/StatSummary.jsx";
import { EmptyState, Spinner } from "../components/ui.jsx";
import { api } from "../lib/api.js";
import { useLive } from "../lib/events.js";
import { useUrlStates } from "../lib/useUrlState.js";

/**
 * Every port in use on the box, and who has a claim on it.
 *
 * The useful question is not which ports exist but which of them can be reached
 * from off the machine. A process on 127.0.0.1 sits behind nginx; one on 0.0.0.0
 * is only as private as the firewall — and two of those here are deploy receivers
 * with hand-written secrets, waiting to be migrated.
 *
 * The registry in the database is empty until projects are onboarded, so what
 * earns this page today is exposure and ownership, not registration.
 */

const EXPOSURE_TONE = {
    loopback: "badge-success",
    tailnet: "badge-queued",
    "all interfaces": "badge-warn"
};

export function Ports() {
    const [data, setData] = useState(null);
    const [{ q: query, exposure, owner }, setUrl] = useUrlStates({ q: "", exposure: "", owner: "" });
    const setQuery = (value) => setUrl("q", value);
    const setExposure = (value) => setUrl("exposure", value);
    const setOwner = (value) => setUrl("owner", value);

    // What listens changes when a process does, which the box's sample carries;
    // what is registered changes with a project.
    useLive(["system", "projects"], () => api.infra.ports().then(setData).catch(() => setData({ ports: [], summary: {} })));

    const shown = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return (data?.ports ?? []).filter((row) => {
            if (needle && !`${row.port} ${row.process ?? ""} ${row.slug ?? ""} ${row.servedAs.join(" ")}`.toLowerCase().includes(needle)) return false;
            if (exposure && row.exposure !== exposure) return false;
            if (owner === "__unowned" && (row.slug || row.process)) return false;
            if (owner === "__managed" && !row.slug) return false;
            return true;
        });
    }, [data, query, exposure, owner]);

    if (!data) return <Spinner className="muted" />;

    const filtered = Boolean(query || exposure || owner);

    return (
        <div className="space-y-4">
            <PageHeader title="Ports" meta={`${data.summary.total} listening`} />

            <StatSummary
                stats={[
                    { label: "Listening", value: data.summary.total },
                    { label: "All interfaces", value: data.summary.exposed, tone: data.summary.exposed > 0 ? "amber" : null },
                    { label: "Loopback only", value: data.summary.loopback },
                    { label: "Unowned", value: data.summary.unowned, tone: data.summary.unowned > 0 ? "amber" : null }
                ]}
            />

            <FilterBar
                query={query}
                onQuery={setQuery}
                placeholder="Search port, process or domain"
                count={filtered ? `${shown.length} of ${data.ports.length}` : `${data.ports.length} ports`}
                filters={[
                    {
                        key: "exposure",
                        label: "Exposure",
                        value: exposure,
                        onChange: setExposure,
                        options: ["loopback", "tailnet", "all interfaces"].map((value) => ({ value, label: value }))
                    },
                    {
                        key: "owner",
                        label: "Owner",
                        value: owner,
                        onChange: setOwner,
                        options: [
                            { value: "__managed", label: "Belongs to a project" },
                            { value: "__unowned", label: "Nothing claims it" }
                        ]
                    }
                ]}
            />

            <DataTable
                rows={shown}
                rowKey={(row) => row.port}
                caption="Listening ports"
                empty={<div className="card"><EmptyState icon={Search} title="No ports match" hint="Try a different search, or clear the filters." /></div>}
                columns={[
                    {
                        key: "port",
                        header: "Port",
                        primary: true,
                        cell: (row) => (
                            <span className="flex min-w-0 items-baseline gap-2">
                                <span className="font-mono text-sm font-medium tabular-nums">{row.port}</span>
                                <span className="muted truncate font-mono text-xs">{row.process ?? "unknown"}</span>
                            </span>
                        )
                    },
                    {
                        key: "exposure",
                        header: "Reachable from",
                        summary: true,
                        cell: (row) => (
                            <span className={EXPOSURE_TONE[row.exposure] ?? "badge-queued"} title={row.addresses.join(", ")}>
                                {row.exposure}
                            </span>
                        )
                    },
                    {
                        key: "project",
                        header: "Project",
                        cell: (row) =>
                            row.slug
                                ? <Link to={`/projects/${row.slug}`} className="link">{row.slug}</Link>
                                : <span className="muted">{row.process ? "unmanaged" : "unowned"}</span>
                    },
                    {
                        key: "served",
                        header: "Served as",
                        hideBelow: "lg",
                        cell: (row) =>
                            row.servedAs.length
                                ? <span className="truncate">{row.servedAs.join(", ")}</span>
                                : <span className="muted">not proxied</span>
                    },
                    {
                        key: "addresses",
                        header: "Bound to",
                        hideBelow: "xl",
                        cell: (row) => <span className="muted font-mono text-xs">{row.addresses.join(", ")}</span>
                    },
                    { key: "band", header: "Band", hideBelow: "xl", cell: (row) => row.band ?? <span className="muted">—</span> }
                ]}
            />
        </div>
    );
}
